import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FormField,
  InputTypes,
  ValidationStatuses,
} from '@cashier/cashier-model';
import { useUIConfiguration } from '@cashier/cashier-shared';
import { cn, InputGenerator, Select } from '@cashier/cashier-ui';

import {
  useCashierConfiguration,
  useFieldToucher,
  useFormPayloadItem,
} from '../../hooks';

import styles from './styles.module.scss';

interface ConfirmationFormFieldProperties {
  field: FormField;
  version: 'v2' | 'v3';
}

export const FieldWrapper = ({
  field,
  version,
}: ConfirmationFormFieldProperties): ReactElement => {
  const { name, type, value, label } = field;

  const { t } = useTranslation();
  const { touch } = useFieldToucher();
  const inputLabelPosition = useUIConfiguration('input', 'label');
  const { validationStatus, isDisabled } = useFormPayloadItem(name);
  const { direction } = useCashierConfiguration();

  const isDefaultLabelPosition = inputLabelPosition === 'default';

  switch (type) {
    case InputTypes.Input:
    case InputTypes.Number:
      return (
        <div
          className={cn({
            [styles.inputContainer]: true,
            [styles.inputContainerV3]: version === 'v3',
          })}
        >
          {isDefaultLabelPosition && (
            <label className={cn([styles.label])}>{label}</label>
          )}
          <div className={cn([styles.inputText])}>
            <InputGenerator
              field={field}
              onChange={(value: string, isFocused: boolean) =>
                touch({ ...field, value }, { interactionCompleted: !isFocused })
              }
              isDisabled={isDisabled}
              labelPosition={inputLabelPosition}
              validationStatus={validationStatus}
              hideLabel
              direction={direction}
            />
          </div>
        </div>
      );

    case InputTypes.Select:
      return (
        <div className={styles.selectContainer}>
          <div className={cn([styles.text, styles.column])}>{field.label}</div>
          <div className={styles.column}>
            <Select
              name={name}
              label={label || ''}
              onChange={(value) =>
                touch({ ...field, value }, { interactionCompleted: true })
              }
              options={field.params?.options || []}
              version={version}
              isDisabled={isDisabled}
              isError={validationStatus === ValidationStatuses.FAILED}
            />
          </div>
        </div>
      );

    default:
      return (
        <div className={styles.defaultContainer}>
          <div className={cn([styles.text, styles.margin, styles.column])}>
            {t(label || '')}
          </div>
          <div className={cn([styles.text, styles.margin, styles.rightColumn])}>
            {value ? value.toString() : ''}
          </div>
        </div>
      );
  }
};
